import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import type { Role } from "@/shared/types/role";
import { ROLE_FILTER_ORDER } from "./constants";
import { resolveChangeRoleError } from "./change-role-error";
import { useChangeUserRole } from "./use-change-user-role";
import { resolveSelfGuard, shouldBlockSelfAction } from "./self-guard";
import type { UserDetail } from "./types";

type View = "form" | "confirm";

interface ChangeRoleDialogProps {
  readonly user: UserDetail | null;
  /**
   * Firebase UID of the currently signed-in admin (from
   * `SessionUser.uid`). Compared against the target user's
   * `firebaseUid` — NOT against `user.id`, which is the internal DB
   * cuid and lives in a different namespace. Confusing the two
   * silently disables the self-demotion guard.
   */
  readonly currentAdminFirebaseUid: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function ChangeRoleDialog({
  user,
  currentAdminFirebaseUid,
  open,
  onOpenChange,
  onSuccess,
}: ChangeRoleDialogProps) {
  const { t } = useTranslation();
  const [nextRole, setNextRole] = useState<Role | null>(null);
  const [view, setView] = useState<View>("form");
  const [error, setError] = useState<string | null>(null);
  const mutation = useChangeUserRole();

  useEffect(() => {
    if (open) {
      setNextRole(user?.role ?? null);
      setView("form");
      setError(null);
    }
  }, [open, user?.id, user?.role]);

  if (!user) return null;

  // Self-demotion guard with fail-safe behavior: blocks on `self`
  // AND `unverified` (missing firebaseUid on either side) so a
  // contract drift that drops the field can't silently re-enable
  // self-role-change. Backend enforces SELF_ROLE_CHANGE_FORBIDDEN
  // (API-1.7) as the authoritative check; this UI gate just saves a
  // round-trip and shows the right copy.
  const guard = resolveSelfGuard(currentAdminFirebaseUid, user.firebaseUid);
  const isBlocked = shouldBlockSelfAction(guard);
  const isUnchanged = nextRole === user.role;
  const canSubmit = !isBlocked && !isUnchanged && nextRole !== null && !mutation.isPending;

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    if (!next && view === "confirm") {
      setView("form");
      return;
    }
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!canSubmit || !nextRole) return;
    setError(null);
    mutation.mutate(
      { id: user.id, input: { role: nextRole } },
      {
        onSuccess: () => {
          setView("form");
          onOpenChange(false);
          onSuccess();
        },
        onError: (err) => {
          setView("form");
          setError(resolveChangeRoleError(err, t));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="change-role-dialog">
        {view === "form" && (
          <div>
            <DialogHeader>
              <DialogTitle>{t("users.changeRole.title")}</DialogTitle>
              <DialogDescription>
                {t("users.changeRole.description", {
                  name: user.displayName || user.email,
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              {guard === "self" && (
                <div
                  data-testid="change-role-self-warning"
                  className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
                >
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"
                    aria-hidden="true"
                  />
                  <p className="font-sans text-xs font-normal text-amber-900 dark:text-amber-200">
                    {t("users.changeRole.selfWarning")}
                  </p>
                </div>
              )}

              {guard === "unverified" && (
                <div
                  data-testid="change-role-unverified-warning"
                  className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
                >
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"
                    aria-hidden="true"
                  />
                  <p className="font-sans text-xs font-normal text-amber-900 dark:text-amber-200">
                    {t("users.unverifiedWarning")}
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <label
                  htmlFor="change-role-select"
                  className="font-sans text-sm font-medium text-foreground"
                >
                  {t("users.changeRole.roleLabel")}
                </label>
                <select
                  id="change-role-select"
                  data-testid="change-role-select"
                  value={nextRole ?? ""}
                  onChange={(e) => setNextRole(e.target.value as Role)}
                  disabled={isBlocked || mutation.isPending}
                  className="block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {ROLE_FILTER_ORDER.map((r) => (
                    <option key={r} value={r}>
                      {t(`users.roleLabel.${r}`)}
                    </option>
                  ))}
                </select>
                <p className="font-sans text-xs font-normal text-muted-foreground">
                  {t("users.changeRole.roleHint")}
                </p>
              </div>

              {error && (
                <p
                  data-testid="change-role-error"
                  className="font-sans text-sm font-medium text-destructive"
                >
                  {error}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t("users.actions.cancel")}
              </Button>
              <Button
                data-testid="confirm-change-role-button"
                onClick={() => setView("confirm")}
                disabled={!canSubmit}
              >
                {t("users.changeRole.next")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {view === "confirm" && nextRole && (
          <div data-testid="confirm-change-role-view" className="mx-auto max-w-md">
            <DialogHeader>
              <DialogTitle>{t("users.changeRole.confirmTitle")}</DialogTitle>
              <DialogDescription>
                {t("users.changeRole.confirmBody", {
                  name: user.displayName || user.email,
                  fromRole: t(`users.roleLabel.${user.role}`),
                  toRole: t(`users.roleLabel.${nextRole}`),
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => setView("form")}
                disabled={mutation.isPending}
              >
                {t("users.actions.cancel")}
              </Button>
              <Button
                variant="destructive"
                data-testid="apply-change-role-button"
                onClick={handleSubmit}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? t("users.changeRole.saving") : t("users.changeRole.confirm")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
